import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { CalendarCheck, Clock, X } from 'lucide-react';
import { toast } from 'sonner';

export default function Appointments() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/bookings').then(r => { setAppointments(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this appointment?')) return;
    try {
      await api.put(`/bookings/${id}/status`, { status: 'cancelled' });
      setAppointments(prev => prev.map(a => a.appointment_id === id ? { ...a, status: 'cancelled' } : a));
      toast.success('Appointment cancelled');
    } catch {
      toast.error('Failed to cancel');
    }
  };

  const statusColor = (s) => {
    const map = {
      pending: 'bg-amber-100 text-amber-800', confirmed: 'bg-blue-100 text-blue-800',
      checked_in: 'bg-indigo-100 text-indigo-800', in_progress: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800', cancelled: 'bg-red-100 text-red-800', no_show: 'bg-gray-100 text-gray-800',
    };
    return map[s] || 'bg-gray-100 text-gray-800';
  };

  const upcoming = appointments.filter(a => ['pending', 'confirmed', 'checked_in', 'in_progress'].includes(a.status));
  const past = appointments.filter(a => ['completed', 'cancelled', 'no_show'].includes(a.status));

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#2D2D2D] border-t-transparent" /></div>;

  const AppointmentCard = ({ appt }) => (
    <Card className="rounded-2xl border-[#E5E5E0] bg-white card-hover" data-testid={`appt-card-${appt.appointment_id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-[#F5F5F0] rounded-xl flex items-center justify-center">
              <CalendarCheck className="w-5 h-5 text-[#2D2D2D]" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-sm font-semibold text-[#2D2D2D]">{appt.date}</p>
              <p className="text-xs text-[#5A5A5A] flex items-center gap-1"><Clock className="w-3 h-3" />{appt.time_slot} - {appt.end_time}</p>
            </div>
          </div>
          <span className={`text-xs font-medium px-3 py-1 rounded-full ${statusColor(appt.status)}`}>
            {appt.status.replace('_', ' ')}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#2D2D2D] font-medium flex items-center gap-0.5">&#8377;{appt.total_price}</p>
          {['pending', 'confirmed'].includes(appt.status) && (
            <Button variant="ghost" size="sm" className="text-xs text-[#D9534F] hover:bg-red-50" onClick={() => handleCancel(appt.appointment_id)} data-testid={`cancel-appt-${appt.appointment_id}`}>
              <X className="w-3 h-3 mr-1" /> Cancel
            </Button>
          )}
        </div>
        {appt.notes && <p className="text-xs text-[#888] mt-2 bg-[#F5F5F0] rounded-lg p-2">{appt.notes}</p>}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="appointments-page">
      <h1 className="text-2xl md:text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Manrope' }}>Appointments</h1>

      <Tabs defaultValue="upcoming" className="space-y-4">
        <TabsList className="bg-[#F5F5F0] rounded-full p-1 inline-flex">
          <TabsTrigger value="upcoming" className="rounded-full data-[state=active]:bg-[#2D2D2D] data-[state=active]:text-white text-sm px-6" data-testid="upcoming-tab">
            Upcoming ({upcoming.length})
          </TabsTrigger>
          <TabsTrigger value="past" className="rounded-full data-[state=active]:bg-[#2D2D2D] data-[state=active]:text-white text-sm px-6" data-testid="past-tab">
            Past ({past.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming" className="space-y-3">
          {upcoming.length === 0 ? (
            <Card className="rounded-2xl border-[#E5E5E0] border-dashed bg-white"><CardContent className="p-8 text-center text-sm text-[#5A5A5A]">No upcoming appointments</CardContent></Card>
          ) : (
            upcoming.map(a => <AppointmentCard key={a.appointment_id} appt={a} />)
          )}
        </TabsContent>
        <TabsContent value="past" className="space-y-3">
          {past.length === 0 ? (
            <Card className="rounded-2xl border-[#E5E5E0] border-dashed bg-white"><CardContent className="p-8 text-center text-sm text-[#5A5A5A]">No past appointments</CardContent></Card>
          ) : (
            past.map(a => <AppointmentCard key={a.appointment_id} appt={a} />)
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
